import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LuLink2Off } from 'react-icons/lu';
import { Tooltip, toast } from '@qodo/design-system';
import { DefaultError } from '@tanstack/react-query';
import { GitProvider } from 'consts/git';
import { Loader2 } from 'lucide-react';
import { useDisconnectGitProviderMutation } from 'network/mutations';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { DisconnectProviderDialog } from '../../DisconnectProviderDialog';
import { USER_MENU_ICON_SIZE } from '../consts';

const DISCONNECT_ICON_CLASS = 'text-purple-400';

type GitProviderDisconnectButtonProps = {
  provider: GitProvider;
};

export const GitProviderDisconnectButton = ({
  provider,
}: GitProviderDisconnectButtonProps) => {
  const { t } = useTranslation();
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const providerToDisconnectRef = useRef<GitProvider | undefined>();

  const {
    mutate: disconnectGitProviderMutate,
    isPending: isDisconnectGitProviderPending,
  } = useDisconnectGitProviderMutation();

  const spawnGitProviderNotifications = spawn(
    'sidebar.footer.user-menu.disconnect-git-provider.notification',
  );

  const openDisconnectDialog = (e: React.MouseEvent) => {
    e.stopPropagation();
    providerToDisconnectRef.current = provider;
    setIsDisconnectDialogOpen(true);
  };

  const closeDisconnectDialog = () => {
    setIsDisconnectDialogOpen(false);
    providerToDisconnectRef.current = undefined;
  };

  const onDialogOpenChange = (open: boolean) => {
    if (!open) {
      closeDisconnectDialog();
    } else {
      setIsDisconnectDialogOpen(true);
    }
  };

  const disconnectCurrentProvider = async (): Promise<void> => {
    const currentProvider = providerToDisconnectRef.current;

    if (!currentProvider) {
      toast.error(spawnGitProviderNotifications('no-provider-error'));

      return;
    }

    try {
      await disconnectGitProviderMutate(currentProvider);
      toast.success(
        spawnGitProviderNotifications('success', { provider: currentProvider }),
      );
      closeDisconnectDialog();
    } catch (err: unknown) {
      const error = err as DefaultError;

      toast.error(error.response?.data?.detail || error.message);
    }
  };

  const getIcon = () => {
    return isDisconnectGitProviderPending ? (
      <Loader2
        className={cn(DISCONNECT_ICON_CLASS, 'animate-loading')}
        size={USER_MENU_ICON_SIZE}
      />
    ) : (
      <LuLink2Off
        className={DISCONNECT_ICON_CLASS}
        size={USER_MENU_ICON_SIZE}
        onClick={openDisconnectDialog}
      />
    );
  };

  return (
    <>
      <Tooltip
        providerProps={{ delayDuration: 0 }}
        triggerProps={{ className: 'ml-auto' }}
        content={
          isDisconnectGitProviderPending ? undefined : t('general.disconnect')
        }
      >
        {getIcon()}
      </Tooltip>

      <DisconnectProviderDialog
        open={isDisconnectDialogOpen}
        onOpenChange={onDialogOpenChange}
        onSubmit={disconnectCurrentProvider}
      />
    </>
  );
};
